import isUndefined from 'lodash/isUndefined';
import { useEffect, useState } from 'react';
import { EditableInput } from 'react-color/es/components/common';
import { isValidHex } from 'react-color/es/helpers/color';
import { ColorHex } from '../../constants';

interface ColorPickerContext {
  hex: string;
  rgb: {
    r: string | number;
    g: string | number;
    b: string | number;
    a?: string | number;
  };
  hsl: {
    h: string | number;
    s: string | number;
    l: string | number;
  };
}

export interface ColorInputProps {
  label: string;
  value: string | number;
  rgb: ColorPickerContext['rgb'];
  hsl: ColorPickerContext['hsl'];
  onChange: (color: any, e: InputEvent) => void;
}

export const ColorInput = ({
  label,
  value,
  onChange,
  rgb,
  hsl,
}: ColorInputProps) => {
  const [selectedHex, setSelectedHex] = useState<string>(value.toString());
  useEffect(() => {
    setSelectedHex(value.toString());
  }, [value]);
  function handleChange(data, e): void {
    if (data.hex && e.target.value.length <= 7) {
      if (e.target.value.length > 4 && isValidHex(data.hex)) {
        onChange(
          {
            hex: data.hex,
            source: 'hex',
          },
          e
        );
      }
      setSelectedHex(e.target.value);
    }
    if (data.r || data.g || data.b) {
      //rgb handler
      const { r = rgb.r, g = rgb.g, b = rgb.b } = data;
      onChange(
        {
          r,
          g,
          b,
          source: 'rgb',
        },
        e
      );
    }
    if (data.a) {
      if (data.a < 0) {
        data.a = 0;
      } else if (data.a > 1) {
        data.a = 1;
      }

      onChange(
        {
          h: hsl.h,
          s: hsl.s,
          l: hsl.l,
          a: Math.round(data.a * 100) / 100,
          source: 'rgb',
        },
        e
      );
    }

    if (data.h || data.s || data.l) {
      // Remove any occurances of '%'.

      const shouldRemovePercentage = (str: string) =>
        typeof str === 'string' && str.includes('%');

      if (shouldRemovePercentage(data.s)) {
        data.s = data.s.replace('%', '');
      }
      if (shouldRemovePercentage(data.l)) {
        data.l = data.l.replace('%', '');
      }

      // We store HSL as a unit interval so we need to override the 1 input to 0.01
      if (data.s === 1) {
        data.s = 0.01;
      } else if (data.l === 1) {
        data.l = 0.01;
      }

      onChange(
        {
          h: data.h || hsl.h,
          s: Number(!isUndefined(data.s) ? data.s : hsl.s),
          l: Number(!isUndefined(data.l) ? data.l : hsl.l),
          source: 'hsl',
        },
        e
      );
    }
  }

  return (
    <EditableInput
      style={{
        input: {
          boxSizing: 'border-box',
          border: '1px solid #EFEFFB',
          borderRadius: '6px',
          font: 'Inter',
          fontWeight: 400,
          fontSize: '14px',
          lineHeight: '20px',
          color: ColorHex.natural600,
          width: '100%',
          height: '30px',
          textAlign: 'left',
          paddingLeft: '10px',
        },
        label: {
          font: 'Inter',
          textTransform: 'uppercase',
          fontWeight: 400,
          fontSize: '12px',
          lineHeight: '16px',
          color: ColorHex.natural300,
          marginBottom: '6px',
        },
        wrap: {
          paddingTop: '16px',
          display: 'flex',
          flexDirection: 'column-reverse',
        },
      }}
      label={label}
      value={selectedHex.toUpperCase()}
      onChange={handleChange}
    />
  );
};
